import { useState, useMemo } from 'react';
import { X, CircleAlert as AlertCircle, ChevronDown, Search, Check, Calendar } from 'lucide-react';
import type { XmCardBrand, XmCustomerPayment, GlobalCustomer, XmCustomer } from '@/types/database';
import {
  CARD_LIMIT_GROUP,
  CARD_GROUP_LIMITS,
  CARD_GROUP_LABELS,
} from '@/types/database';
import { getCardBrandColor } from './cardBrandColors';

interface Props {
  globalCustomers: GlobalCustomer[];
  preselectedCustomerName: string;
  existingPayments?: XmCustomerPayment[];
  allPayments: XmCustomerPayment[];
  allCustomers: XmCustomer[];
  onClose: () => void;
  onSave: (customerName: string, cardBrand: XmCardBrand, chargedAmount: number, fullLimit: number, paymentDate: string) => void;
}

interface CustomerCardOption {
  customerName: string;
  cardBrand: XmCardBrand;
  label: string;
}

function CustomerPickerModal({
  options,
  selected,
  onSelect,
  onClose,
}: {
  options: CustomerCardOption[];
  selected: CustomerCardOption | null;
  onSelect: (opt: CustomerCardOption) => void;
  onClose: () => void;
}) {
  const [searchTerm, setSearchTerm] = useState('');

  const filtered = searchTerm.trim()
    ? options.filter(o => o.label.toLowerCase().includes(searchTerm.toLowerCase()))
    : options;

  const grouped = useMemo(() => {
    const map = new Map<string, CustomerCardOption[]>();
    for (const opt of filtered) {
      const list = map.get(opt.customerName) || [];
      list.push(opt);
      map.set(opt.customerName, list);
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered]);

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-md flex items-center justify-center z-[60] p-4">
      <div className="glass-modal rounded-2xl max-w-md w-full max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <h3 className="text-sm font-bold text-white">Select Customer & Card</h3>
          <button
            onClick={onClose}
            className="text-white hover:text-white p-1 glass-hover rounded-lg"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-3 border-b border-white/10">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white" />
            <input
              type="text"
              placeholder="Search customers or cards..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              autoFocus
              className="w-full pl-9 pr-4 py-2.5 text-sm bg-white/10 border border-white/10 rounded-xl focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none transition-all duration-200 placeholder:text-white/40"
            />
          </div>
        </div>

        <div className="flex-1 overflow-auto p-2">
          {grouped.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10">
              <p className="text-xs text-white">No customers found.</p>
              <p className="text-[11px] text-white mt-0.5">Add them in Database first.</p>
            </div>
          ) : (
            grouped.map(([customerName, cards]) => (
              <div key={customerName} className="mb-1">
                <p className="text-[10px] font-bold text-white uppercase tracking-wider px-3 pt-3 pb-1.5">
                  {customerName}
                </p>
                {cards.map((opt, i) => {
                  const color = getCardBrandColor(opt.cardBrand);
                  const isSelected = selected?.label === opt.label;
                  return (
                    <button
                      key={i}
                      onClick={() => onSelect(opt)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all duration-150 ${
                        isSelected
                          ? 'bg-teal-500/10 ring-1 ring-teal-200'
                          : 'glass-hover'
                      }`}
                    >
                      <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${color.dot}`} />
                      <span className={`flex-1 text-xs ${isSelected ? 'text-teal-700 font-semibold' : 'text-white'}`}>
                        {opt.cardBrand}
                      </span>
                      {isSelected && (
                        <Check size={14} className="shrink-0 text-teal-600" />
                      )}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default function AddPaymentModal({
  globalCustomers,
  preselectedCustomerName,
  allPayments,
  allCustomers,
  onClose,
  onSave,
}: Props) {
  const options = useMemo<CustomerCardOption[]>(() => {
    const result: CustomerCardOption[] = [];
    for (const gc of globalCustomers) {
      for (const card of gc.owned_cards) {
        result.push({
          customerName: gc.name,
          cardBrand: card.type,
          label: `${gc.name} - ${card.type}`,
        });
      }
    }
    return result.sort((a, b) => a.label.localeCompare(b.label));
  }, [globalCustomers]);

  const preselectedOption = preselectedCustomerName
    ? options.find(o => o.customerName === preselectedCustomerName) || null
    : null;

  const [selected, setSelected] = useState<CustomerCardOption | null>(preselectedOption);
  const [chargedAmount, setChargedAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [pickerOpen, setPickerOpen] = useState(false);

  const paymentsForCustomer = useMemo(() => {
    if (!selected) return [];
    const customer = allCustomers.find(c => c.name === selected.customerName);
    if (!customer) return [];
    return allPayments.filter(p => p.customer_id === customer.id);
  }, [selected, allCustomers, allPayments]);

  const groupTotals = useMemo(() => {
    const totals = new Map<string, number>();
    for (const p of paymentsForCustomer) {
      const group = CARD_LIMIT_GROUP[p.card_brand as XmCardBrand];
      if (group) {
        totals.set(group, (totals.get(group) || 0) + Number(p.charged_amount));
      }
    }
    return totals;
  }, [paymentsForCustomer]);

  const selectedGroup = selected ? CARD_LIMIT_GROUP[selected.cardBrand] : null;
  const groupFullLimit = selectedGroup ? CARD_GROUP_LIMITS[selectedGroup] : 0;
  const groupUsed = selectedGroup ? (groupTotals.get(selectedGroup) || 0) : 0;
  const groupAvailable = groupFullLimit - groupUsed;

  const charged = parseFloat(chargedAmount) || 0;
  const availableAfter = groupAvailable - charged;

  function handleSubmit() {
    if (!selected) { alert('Select a customer and card'); return; }
    if (charged <= 0) { alert('Enter a valid charge amount'); return; }
    if (charged > groupAvailable) { alert(`Charge exceeds available limit of $${groupAvailable.toFixed(2)}`); return; }
    if (!paymentDate) { alert('Select a payment date'); return; }
    onSave(selected.customerName, selected.cardBrand, charged, groupFullLimit, paymentDate);
  }

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-md flex items-center justify-center z-50 p-4">
      <div className="glass-modal rounded-2xl max-w-sm w-full max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div>
            <h2 className="text-base font-bold text-white">Add Customer</h2>
            {selected && (
              <p className="text-xs text-white mt-0.5">{selected.customerName}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-white p-1 glass-hover rounded-lg"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-auto p-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-white mb-1.5">Customer &amp; Card</label>
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              className="w-full flex items-center justify-between px-4 py-2.5 bg-white/10 border border-white/10 rounded-xl text-sm text-left focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none transition-all duration-200 hover:border-slate-300"
            >
              {selected ? (
                <div className="flex items-center gap-2 min-w-0">
                  <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${getCardBrandColor(selected.cardBrand).dot}`} />
                  <span className="truncate text-white">{selected.label}</span>
                </div>
              ) : (
                <span className="text-white">Select customer & card...</span>
              )}
              <ChevronDown size={14} className="shrink-0 text-white ml-2" />
            </button>
          </div>

          {selected && selectedGroup && (
            <div className="glass-inner rounded-xl p-3 space-y-2">
              <div className="flex items-center gap-1.5 mb-1">
                <span className="text-[10px] font-semibold text-white uppercase tracking-wider">
                  {CARD_GROUP_LABELS[selectedGroup]} Limit
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <p className="text-[10px] text-white">Full Limit</p>
                  <p className="text-sm font-bold text-white">${groupFullLimit.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-[10px] text-white">Used</p>
                  <p className="text-sm font-bold text-white">${groupUsed.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-[10px] text-white">Available</p>
                  <p className={`text-sm font-bold ${groupAvailable > 0 ? 'text-emerald-800' : 'text-red-600'}`}>
                    ${groupAvailable.toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-white mb-1.5">Payment Date</label>
            <div className="relative">
              <Calendar size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <input
                type="date"
                value={paymentDate}
                onChange={e => setPaymentDate(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-white/10 border border-white/10 rounded-xl text-sm focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none transition-all duration-200"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-white mb-1.5">Charge Amount (USD)</label>
            <input
              type="number"
              step="0.01"
              placeholder="0.00"
              value={chargedAmount}
              onChange={e => setChargedAmount(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSubmit()}
              className="w-full px-4 py-2.5 bg-white/10 border border-white/10 rounded-xl text-sm focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none transition-all duration-200 placeholder:text-white/40"
            />
          </div>

          {charged > 0 && selected && (
            <div className={`rounded-xl p-3 text-center ${availableAfter >= 0 ? 'bg-emerald-500/10' : 'bg-red-500/10'}`}>
              <span className="text-[10px] text-white uppercase tracking-wide">Available After This Charge</span>
              <p className={`text-lg font-bold ${availableAfter >= 0 ? 'text-emerald-800' : 'text-red-600'}`}>
                ${availableAfter.toFixed(2)}
              </p>
            </div>
          )}

          {selected && groupAvailable <= 0 && (
            <div className="flex items-center gap-2 bg-red-500/10 text-red-700 rounded-xl px-3 py-2.5 text-xs">
              <AlertCircle size={14} className="shrink-0" />
              <span>This limit group is fully used. No more charges can be added.</span>
            </div>
          )}
        </div>

        <div className="flex gap-3 p-5 border-t border-white/10">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 text-sm glass-button text-white rounded-xl font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!selected || groupAvailable <= 0}
            className="flex-1 px-4 py-2.5 text-sm glass-button-active text-white rounded-xl font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Save
          </button>
        </div>
      </div>

      {pickerOpen && (
        <CustomerPickerModal
          options={options}
          selected={selected}
          onSelect={(opt) => { setSelected(opt); setPickerOpen(false); }}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
}
